

(() => {
  'use strict';
  if (window.DD3DKitSyncInstalled) return;
  window.DD3DKitSyncInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const api = (path) => root + 'cart/' + path + '?opus=true&dd3d_sync=1';
  const roles = { diffuser: 'diffuseur', parfum: 'parfum' };

  let timer = null;
  let processing = false;
  let runAgain = false;
  let lastValid = new Map();
  let unsafe = true;
  let active = null;
  let pendingMutations = 0;
  let mutationTail = Promise.resolve();
  let lastEdit = 0;

  function markUnsafe(value) {
    unsafe = value;
    document.documentElement?.toggleAttribute('data-dd3d-kit-unverified', value);
  }
  if (document.head) {
    const style = document.createElement('style');
    style.textContent = '[data-dd3d-kit-unverified] .cart__dynamic-checkout-buttons,[data-dd3d-kit-unverified] .additional-checkout-buttons,[data-dd3d-kit-unverified] shopify-accelerated-checkout-cart{visibility:hidden!important;pointer-events:none!important}';
    document.head.appendChild(style);
  }
  markUnsafe(true);

  function positiveInteger(value, fallback = 0) {
    const n = Number(value);
    return Number.isSafeInteger(n) && n > 0 ? n : fallback;
  }

  function cartMutation(method, url) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return false;
    try {
      const path = new URL(url, location.href).pathname;
      return /(?:^|\/)cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(path);
    } catch (_) { return false; }
  }

  async function jsonRequest(path, init = {}) {
    const response = await nativeFetch(api(path), {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init
    });
    let data;
    try { data = await response.json(); } catch (_) { data = {}; }
    if (!response.ok) {
      throw new Error(data.description || data.message || `Shopify HTTP ${response.status}`);
    }
    return data;
  }

  async function readCart() {
    const response = await nativeFetch(root + 'cart.js', {
      credentials: 'same-origin', cache: 'no-store'
    });
    if (!response.ok) throw new Error('Impossible de charger le panier Shopify.');
    return response.json();
  }

  function groupsFrom(cart) {
    const groups = new Map();
    (cart.items || []).forEach(item => {
      const props = item.properties || {};
      const id = props._dd3d_kit;
      const role = props._dd3d_role;
      if (!id || (role !== roles.diffuser && role !== roles.parfum)) return;
      let group = groups.get(id);
      if (!group) {
        group = { id, diffuser: [], parfum: [] };
        groups.set(id, group);
      }
      group[role === roles.diffuser ? 'diffuser' : 'parfum'].push(item);
    });
    return groups;
  }

  const total = lines => lines.reduce((sum, item) => sum + Number(item.quantity), 0);

  function metrics(group) {
    const parent = group.diffuser[0];
    const source = parent || group.parfum[0];
    const ratio = positiveInteger(source?.properties?._dd3d_ratio);
    const parfumId = String(source?.properties?._dd3d_parfum_id || '');
    if (![1, 2].includes(ratio) || !positiveInteger(parfumId)) {
      throw new Error('Métadonnées du kit incomplètes.');
    }
    for (const item of [...group.diffuser, ...group.parfum]) {
      if (Number(item.properties._dd3d_ratio) !== ratio ||
          String(item.properties._dd3d_parfum_id) !== parfumId ||
          (item.properties._dd3d_role === roles.parfum && String(item.variant_id) !== parfumId) ||
          (item.properties._dd3d_role === roles.diffuser && parent && String(item.variant_id) !== String(parent.variant_id))) {
        throw new Error('Références incompatibles dans un kit.');
      }
    }
    return { ratio, parfumId, quantity: total(group.diffuser), parfumQuantity: total(group.parfum) };
  }

  function coherentGroups(cart) {
    try {
      if ((cart.items || []).some(item => unlinkedDiffuser(item, cart))) return false;
      const tagged = (cart.items || []).filter(item => item.properties?._dd3d_kit);
      if (tagged.some(item => ![roles.diffuser, roles.parfum].includes(item.properties._dd3d_role))) return false;
      for (const group of groupsFrom(cart).values()) {
        const m = metrics(group);
        if (!group.diffuser.length || !group.parfum.length || m.parfumQuantity !== m.quantity * m.ratio) return false;
      }
      return true;
    } catch (_) { return false; }
  }

  function unlinkedDiffuser(item, cart) {
    const p = item.properties || {};
    if (p._dd3d_kit) return false;
    const known = [15333673435518, 15706705559934, 16057815990654].includes(Number(item.product_id)) ||
      (cart.items || []).some(other => other.properties?._dd3d_role === roles.diffuser && String(other.variant_id) === String(item.variant_id));
    return known && Boolean(p.Marque || p.Parfum || /la casa|enez|noham/i.test(item.variant_title || ''));
  }

  async function restoreLinks(cart) {
    for (let attempt = 0; attempt < 60; attempt++) {
      const item = (cart.items || []).find(line => unlinkedDiffuser(line, cart));
      if (!item) return cart;
      const candidates = [...groupsFrom(cart).values()].filter(group => group.diffuser.some(parent => {
        if (String(parent.variant_id) !== String(item.variant_id)) return false;
        return ['Marque', 'Parfum', 'Format', 'Contenance parfum'].every(key =>
          String(parent.properties[key] || '') === String(item.properties?.[key] || ''));
      }));
      if (candidates.length !== 1) throw new Error('Ancien kit sans liaison : retirez-le puis ajoutez-le depuis le configurateur.');
      const source = candidates[0].diffuser[0].properties;
      const properties = { ...item.properties };
      for (const key of ['_dd3d_kit', '_dd3d_role', '_dd3d_ratio', '_dd3d_parfum_id']) properties[key] = source[key];
      await jsonRequest('change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties })
      });
      cart = await readCart();
    }
    throw new Error('Trop de lignes sans liaison.');
  }

  function remember(cart) {
    if (!coherentGroups(cart)) return;
    lastValid = new Map([...groupsFrom(cart).values()].map(group => [group.id, metrics(group)]));
    markUnsafe(false);
    document.getElementById('dd3d-kit-alert')?.remove();
  }

  async function resize(cart, groupId, role, desired) {
    const group = groupsFrom(cart).get(groupId);
    if (!group) return cart;
    const lines = group[role];
    const current = total(lines);
    if (current === desired) return cart;
    if (current < desired) {
      if (lines.length) return change(lines[0], Number(lines[0].quantity) + desired - current);
      return addMissing(group, desired);
    }
    const last = lines[lines.length - 1];
    return change(last, Math.max(0, Number(last.quantity) - (current - desired)));
  }

  async function change(item, quantity) {
    if (!item || !item.key) throw new Error('Clé de ligne Shopify manquante.');
    await jsonRequest('change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: String(item.key), quantity })
    });
    return readCart();
  }

  async function addMissing(group, quantity) {
    const parent = group.diffuser[0];
    const id = Number(parent.properties._dd3d_parfum_id);
    if (!Number.isSafeInteger(id) || id <= 0) {
      throw new Error('Référence du parfum manquante dans ce kit.');
    }
    await jsonRequest('add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{
        id,
        quantity,
        properties: { _dd3d_kit: group.id, _dd3d_role: roles.parfum, _dd3d_ratio: String(parent.properties._dd3d_ratio), _dd3d_parfum_id: String(id) }
      }] })
    });
    return readCart();
  }

  function notify(message) {
    if (!document.body) return;
    let element = document.getElementById('dd3d-kit-alert');
    if (!element) {
      element = document.createElement('div');
      element.id = 'dd3d-kit-alert';
      element.setAttribute('role', 'alert');
      element.style.cssText = 'position:fixed;z-index:2147483647;left:12px;right:12px;bottom:24px;max-width:560px;margin:auto;padding:13px 16px;background:#171717;color:#fff;border:1px solid #b39155;border-radius:10px;font:13px/1.45 Arial,sans-serif;box-shadow:0 5px 20px #0003';
      document.body.appendChild(element);
    }
    element.textContent = message;
  }

  function informCart(cart) {
    if (typeof window.OpusSetCartData === 'function') window.OpusSetCartData(cart);
    const cartItems = document.querySelector('cart-items');
    if (typeof cartItems?.onCartUpdate === 'function') cartItems.onCartUpdate();
    const detail = { source: 'dd3d-kit-sync', cart };
    document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', { detail }));
    window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      try {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'dd3d-kit-sync', cartData: cart
        });
      } catch (err) { console.warn('[DD3D] Événement thème', err); }
    }
  }

  async function reconcile(cart) {
    let changed = false;
    const targets = new Map();
    const capped = new Set();
    for (let attempt = 0; attempt < 120; attempt++) {
      let work = null;
      for (const group of groupsFrom(cart).values()) {
        const m = metrics(group);
        if (!targets.has(group.id)) {
          const before = lastValid.get(group.id);
          let target = m.quantity;
          if (!group.diffuser.length) target = 0;
          else if (before && m.quantity === before.quantity && m.parfumQuantity !== before.parfumQuantity) {
            target = Math.floor(m.parfumQuantity / m.ratio);
          }
          targets.set(group.id, target);
        }
        const target = targets.get(group.id);
        if (m.quantity !== target || m.parfumQuantity !== target * m.ratio) {
          work = { group, m, target };
          break;
        }
      }
      if (!work) {
        if (!coherentGroups(cart)) throw new Error('Kit incomplet ou propriétés invalides.');
        return { cart, changed };
      }
      const { group, m, target } = work;
      try {
        const role = m.quantity !== target ? 'diffuser' : 'parfum';
        const desired = role === 'diffuser' ? target : target * m.ratio;
        const oldTotal = role === 'diffuser' ? m.quantity : m.parfumQuantity;
        cart = await resize(cart, group.id, role, desired);
        const updated = groupsFrom(cart).get(group.id);
        const actual = updated ? total(updated[role]) : 0;
        if (actual === oldTotal || (desired > oldTotal && actual < desired)) {
          throw new Error('Quantité refusée par Shopify.');
        }
        changed = true;
      } catch (error) {
        cart = await readCart();
        if (capped.has(group.id)) throw error;
        capped.add(group.id);
        const current = groupsFrom(cart).get(group.id);
        const possible = current ? Math.min(total(current.diffuser), Math.floor(total(current.parfum) / m.ratio)) : 0;
        targets.set(group.id, Math.min(target, possible));
        changed = true;
        notify('Stock de parfum insuffisant. La quantité du kit a été ajustée.');
      }
    }
    throw new Error('Synchronisation incomplète.');
  }

  function execute() {
    if (active) { runAgain = true; return active; }
    active = executeInternal().finally(() => { active = null; });
    return active;
  }

  async function executeInternal() {
    if (processing) { runAgain = true; return; }
    processing = true;
    markUnsafe(true);
    try {
      do {
        runAgain = false;
        const initial = await restoreLinks(await readCart());
        const result = await reconcile(initial);
        if (!coherentGroups(result.cart)) {
          throw new Error('Des kits ne sont pas synchronisés.');
        }
        remember(result.cart);
        if (result.changed) informCart(result.cart);
      } while (runAgain);
    } catch (error) {
      markUnsafe(true);
      console.error('[DD3D] Synchronisation kit :', error);
      notify('Impossible de synchroniser les kits. Actualisez le panier et vérifiez les quantités avant de payer.');
    } finally {
      processing = false;
      if (runAgain) schedule();
    }
  }

  function schedule(delay = 180) {
    clearTimeout(timer);
    timer = setTimeout(execute, delay);
  }


  window.fetch = function (...args) {
    const resource = args[0];
    const options = args[1] || {};
    const url = typeof resource === 'string' ? resource : resource?.url || String(resource);
    const method = options.method || (resource && resource.method) || 'GET';
    const watch = cartMutation(method, url);
    if (!watch) return nativeFetch(...args);
    markUnsafe(true);
    pendingMutations++;
    lastEdit = Date.now();
    return nativeFetch(...args).finally(() => {
      pendingMutations--;
      schedule(700);
    });
  };

  if (window.XMLHttpRequest) {
    const open = XMLHttpRequest.prototype.open;
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__dd3dWatch = cartMutation(method, url);
      return open.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (...args) {
      if (this.__dd3dWatch) {
        markUnsafe(true);
        pendingMutations++;
        this.addEventListener('loadend', () => { pendingMutations--; schedule(); }, { once: true });
      }
      return send.apply(this, args);
    };
  }

  document.addEventListener('cart:updated', event => {
    if (event.detail?.source !== 'dd3d-kit-sync') schedule(220);
  });
  document.addEventListener('shopify:cart:lines-update', event => {
    if (event.detail?.source !== 'dd3d-kit-sync') schedule(220);
  });
  window.addEventListener('OpusCartChange', () => schedule(700));
  window.addEventListener('OpusCartRendered', () => schedule(700));
  window.addEventListener('pageshow', () => schedule(100));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule(180);
  });


  let checkingCheckout = false;
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (target?.closest('.cd-number-input-button,button[aria-label="Remove item"],quantity-input button,cart-remove-button')) {
      lastEdit = Date.now();
      markUnsafe(true);
    }
  }, true);
  document.addEventListener('input', event => {
    if (event.target?.matches('.cd-number-input-field,input[name="updates[]"]')) {
      lastEdit = Date.now();
      markUnsafe(true);
    }
  }, true);
  const postcodeKey = 'dd3d.pickup.postcode';
  let rememberedPostcode = '';
  try {
    const stored = localStorage.getItem(postcodeKey);
    if (/^[0-9]{5}$/.test(stored)) rememberedPostcode = stored;
  } catch {}

  function rememberPostcode(value) {
    rememberedPostcode = value;
    try {
      if (value) localStorage.setItem(postcodeKey, value);
      else localStorage.removeItem(postcodeKey);
    } catch {}
    updatePostcodeControls();
  }

  function updatePostcodeControls() {
    for (const button of document.querySelectorAll('button.cd-checkout-section-button,button[name="checkout"],input[name="checkout"]')) {
      let edit = button.previousElementSibling;
      if (!edit?.hasAttribute('data-dd3d-edit-postcode')) {
        if (!rememberedPostcode || window.Shopify?.country !== 'FR') continue;
        edit = document.createElement('button');
        edit.type = 'button';
        edit.className = 'link';
        edit.dataset.dd3dEditPostcode = '';
        edit.onclick = async () => {
          const value = await askPickupPostcode();
          if (value !== null) rememberPostcode(value);
        };
        button.before(edit);
      }
      edit.hidden = !rememberedPostcode || window.Shopify?.country !== 'FR';
      const label = 'Point de retrait · ' + rememberedPostcode + ' · Modifier';
      if (edit.textContent !== label) edit.textContent = label;
    }
  }
  let updateFrame;
  new MutationObserver(() => {
    if (updateFrame) return;
    updateFrame = requestAnimationFrame(() => { updateFrame = 0; updatePostcodeControls(); });
  }).observe(document.body, {childList:true, subtree:true});
  updatePostcodeControls();

  function askPickupPostcode() {
    return new Promise(resolve => {
      const dialog = document.createElement('dialog');
      dialog.setAttribute('aria-labelledby', 'dd3d-pickup-title');
      dialog.style.cssText = 'max-width:min(38rem,90vw);padding:2rem;border:1px solid #ddd;border-radius:12px;color:inherit;background:white';
      dialog.innerHTML = '<h2 id="dd3d-pickup-title">Livraison en point de retrait</h2><p>Votre code postal sera mémorisé sur cet appareil pour vos prochains paniers.</p><label>Code postal français<br><input inputmode="numeric" autocomplete="postal-code" maxlength="5" pattern="[0-9]{5}" required style="padding:.7rem;width:10rem"></label><p><button type="button" class="button">Voir les points de retrait</button></p><button type="button" class="link">Continuer sans code postal</button>';
      const input = dialog.querySelector('input');
      input.value = rememberedPostcode;
      const [confirm, skip] = dialog.querySelectorAll('button');
      const finish = value => { dialog.close(); dialog.remove(); resolve(value); };
      confirm.onclick = () => { if (input.reportValidity()) finish(input.value); };
      input.onkeydown = e => { if (e.key === 'Enter') { e.preventDefault(); confirm.click(); } };
      skip.onclick = () => finish('');
      dialog.oncancel = e => { e.preventDefault(); finish(null); };
      document.body.append(dialog);
      dialog.showModal();
      input.focus();
    });
  }

  async function preferPickup(cart, postcode) {
    const options = {signal: AbortSignal.timeout(5000)};
    const response = await fetch('https://geo.api.gouv.fr/communes?codePostal=' + postcode + '&fields=centre,population&format=json', options);
    if (!response.ok) throw new Error();
    const towns = await response.json();
    const town = towns.sort((a,b) => (b.population || 0) - (a.population || 0))[0];
    const [longitude, latitude] = town.centre.coordinates;
    const query = 'mutation($id:ID!,$buyer:CartBuyerIdentityInput!){cartBuyerIdentityUpdate(cartId:$id,buyerIdentity:$buyer){cart{checkoutUrl} userErrors{message}}}';
    const delivery = {deliveryMethod:['PICKUP_POINT'], coordinates:{latitude,longitude,countryCode:'FR'}};
    const result = await fetch('/api/2026-10/graphql.json', {
      method:'POST', signal:AbortSignal.timeout(6000), headers:{'Content-Type':'application/json'},
      body:JSON.stringify({query, variables:{id:'gid://shopify/Cart/' + cart.token, buyer:{preferences:{delivery}}}})
    });
    const payload = (await result.json()).data?.cartBuyerIdentityUpdate;
    if (!payload?.cart?.checkoutUrl || payload.userErrors.length) throw new Error();
    return payload.cart.checkoutUrl;
  }

  async function guardCheckout(event) {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const control = event.type === 'submit'
      ? event.submitter
      : target?.closest('a[href*="/checkout"],button[name="checkout"],input[name="checkout"],button.cd-checkout-section-button');
    const form = event.type === 'submit' ? event.target : control?.form;
    const checkoutForm = form && (/\/checkout(?:[/?]|$)/.test(form.action || '') ||
      (event.type === 'submit' && (event.submitter?.name === 'checkout' ||
       (!event.submitter && form.querySelector('[name="checkout"]')))));
    if (!control && !checkoutForm) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingCheckout) return;
    checkingCheckout = true;
    try {
      const postcode = window.Shopify?.country === 'FR' ? (rememberedPostcode || await askPickupPostcode()) : '';
      if (postcode === null) return;
      if (postcode && postcode !== rememberedPostcode) rememberPostcode(postcode);
      for (let attempt = 0; attempt < 40 && (pendingMutations || Date.now() - lastEdit < 1000); attempt++) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      await mutationTail;
      await execute();
      if (unsafe || processing || pendingMutations) throw new Error('Panier en cours de modification.');
      const cart = await readCart();
      if (!coherentGroups(cart)) throw new Error('Kit incomplet.');
      const link = control?.closest('a[href]');
      let destination = link ? link.href : root + 'checkout';
      if (postcode) {
        try { destination = await preferPickup(cart, postcode); }
        catch { notify('Sélectionnez votre mode de livraison à l’étape du paiement.'); }
      }
      location.assign(destination);
    } catch (error) {
      markUnsafe(true);
      notify('Le panier doit contenir des kits complets avant le paiement. Vérifiez les quantités puis réessayez.');
    } finally { checkingCheckout = false; }
  }
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  window.DD3DKitSync = { verify: execute, isSafe: () => !unsafe && !processing && !pendingMutations };
  schedule(50);
})();
